import React, { useMemo } from "react";
import { EdgeLabelRenderer, Position, getSmoothStepPath } from "@xyflow/react";
import { cn } from "@/utils/theme";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { EdgeData, NodeData } from "@/workflow_api/types/workflow";

interface WorkflowEdgeProps {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  style?: React.CSSProperties;
  data?: EdgeData;
  markerEnd?: string;
  selected?: boolean;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export function ExecutionEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  selected,
}: WorkflowEdgeProps) {
  const { nodeConnecting, getNodeById, executionData } = useWorkflowStore();
  const isConnecting = nodeConnecting === data?.source.id;

  const label = useMemo(() => {
    const sourceNode = getNodeById(data!.source.id!);
    const _data = sourceNode?.data as unknown as NodeData;
    if (_data?.connections && _data?.connections?.[data!.source.handle!]) {
      return _data.connections[data!.source.handle!].display_name;
    }
  }, [data, getNodeById]);

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    offset: isConnecting ? 0 : 20,
    centerX: (sourceX + targetX) / 2,
    centerY: (sourceY + targetY) / 2,
  });

  const getEdgeColor = () => {
    if (executionData) {
      const nodeExecutions = executionData.node_executions
        .filter((execution) => {
          const targetNode = getNodeById(data?.target.id || "");
          const targetNodeData = targetNode?.data as unknown as NodeData;
          const isMergeNode = targetNodeData?.node_type === "MERGE";

          const nodeIdToCheck = isMergeNode ? data?.source.id : data?.target.id;

          const isMatch = execution.node_id === nodeIdToCheck;

          return isMatch;
        })
        .sort((a, b) => {
          // if ended_at is null, put it last
          if (a.ended_at === null) return 1;
          if (b.ended_at === null) return -1;
          // sort by ended_at descending
          return (
            new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime()
          );
        });
      const nodeExecution = nodeExecutions[0];
      if (nodeExecution) {
        switch (nodeExecution.status) {
          case "COMPLETED":
            return "#22c55e";
          case "FAILED":
            return "#dc2626";
          case "RUNNING":
            return "#3b82f6";
          case "PENDING":
            return "#f59e0b";
          case "CANCELLED":
            return "#f97316";
          case "EXPIRED":
            return "#7c2d12";
          case "UNPROCESSED":
            return "#9ca3af";
          default:
            return "#6b7280";
        }
      }
    }
    if (selected) return "var(--border-action)";
    return "var(--border-neutral)";
  };

  const getEdgeWidth = () => {
    if (selected) return 2;
    return 2;
  };

  return (
    <>
      <path
        id={id}
        style={{
          stroke: getEdgeColor(),
          strokeWidth: getEdgeWidth(),
          fill: "none",
        }}
        className={cn(
          "react-flow__edge-path peer",
          // isAnimated && "animated"
        )}
        d={edgePath}
        markerEnd={markerEnd}
        data-testid={"edge-path"}
      />
      <EdgeLabelRenderer>
        {label && (
          <div
            style={{
              position: "absolute",
              transform: `translate(0%, -50%) translate(${sourceX}px,${sourceY}px)`,
              fontSize: 12,
              pointerEvents: "all",
              zIndex: 50,
            }}
            className="no-drag nopan bg-neutral-object rounded-lg w-auto border border-neutral"
          >
            <TooltipWrapper title={label}>
              <div className="edge-label min-w-[40px] max-w-[148px] px-1.5 text-body-loud whitespace-nowrap truncate">
                {label}
              </div>
            </TooltipWrapper>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}

const TooltipWrapper = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={4}
        className="max-w-[240px] break-all"
      >
        {title}
      </TooltipContent>
    </Tooltip>
  );
};
